import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useThemeContext } from 'context/ThemeContext/ThemeContext';
import { SerialNumberRouterModuleData } from 'types/DigitalHubTypes';
import { showToast } from 'components/atomic/Toast/Toast';
import { useTranslation } from 'react-i18next';
import { isPreviewMode } from 'utils/preview';
import { useAPI } from 'utils/api';
import Input from 'components/atomic/Input';
import useRedirectLink from 'hooks/useRedirectLink';
import ProductButton from 'components/atomic/ProductButton';

type SerialRputerProps = {
  brandId: string;
  buttonStyle?: string;
  serialModule: SerialNumberRouterModuleData;
};

// Wait 500ms after the last keystroke before validating so the API is not
// hit on every character while the user is still typing (AQ-23).
const VALIDATION_DEBOUNCE_MS = 500;

const SerialRouter: React.FC<SerialRputerProps> = ({
  brandId,
  buttonStyle,
  serialModule,
}) => {
  const [serialNumber, setSerialNumber] = useState<string>('');
  const [serialError, setSerialError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  // Cached redirect route from the most recent successful validation. The
  // button click uses this to call window.open synchronously — on Safari iOS
  // any await between the user gesture and window.open triggers the popup
  // blocker (AQ-23).
  const [redirectRoute, setRedirectRoute] = useState<string | null>(null);
  // Monotonic counter that lets us drop stale API responses when the user
  // keeps typing and multiple debounced validations overlap.
  const validationGeneration = useRef(0);
  const redirectToExternalLink = useRedirectLink();
  const { brandTheme } = useThemeContext();
  const { t } = useTranslation('translation', {
    keyPrefix: 'digitalHub',
  });

  const endpoint = `digitalhub/${brandId}?serialNumber=${encodeURIComponent(
    serialNumber
  )}`;
  const [lookupSerialNumber] = useAPI({
    endpoint,
    method: 'GET',
  });

  // Pre-validate the serial number 500ms after the user stops typing so the
  // redirect URL is cached and the button click below can open the new tab
  // synchronously — the whole point of this flow is to remove the async gap
  // between the click gesture and window.open (AQ-23).
  useEffect(() => {
    if (!serialNumber) {
      // Empty input — invalidate any in-flight validation and reset state.
      validationGeneration.current += 1;
      setRedirectRoute(null);
      setSerialError('');
      setLoading(false);
      return;
    }

    const currentGeneration = (validationGeneration.current += 1);

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const route = await lookupSerialNumber(undefined, null, true);
        if (currentGeneration !== validationGeneration.current) return;
        if (route) {
          setRedirectRoute(route);
          setSerialError('');
          // Toast fires on successful validation (not on click), so the user
          // sees confirmation before the button is pressed.
          showToast({
            message: t('serialNumberFound'),
            type: 'success',
          });
        } else {
          setRedirectRoute(null);
          setSerialError(
            serialModule?.failureMessage
              ? serialModule.failureMessage
              : t('serialNumberNotFoundError')
          );
        }
      } catch (error: any) {
        if (currentGeneration !== validationGeneration.current) return;
        setRedirectRoute(null);
        setSerialError(
          serialModule?.failureMessage
            ? serialModule.failureMessage
            : t('serialNumberNotFoundError')
        );
      } finally {
        // Only the latest validation clears the loading flag so a stale
        // response cannot briefly un-spinner the button while a newer call
        // is still in flight.
        if (currentGeneration === validationGeneration.current) {
          setLoading(false);
        }
      }
    }, VALIDATION_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialNumber]);

  // Must stay synchronous — no await, no setTimeout — so window.open fires
  // inside the same event tick as the user gesture and Safari iOS keeps
  // transient activation alive (AQ-23).
  const handleRedirect = () => {
    if (!redirectRoute) return;
    redirectToExternalLink(
      `${window.location.protocol}//${window.location.host}/c/${redirectRoute}`,
      '_blank',
      '',
      false
    );
  };

  const getCornerRadius = () => {
    if (buttonStyle === 'largeCorner') return 'rounded-[38px]';
    else if (buttonStyle === 'smallCorner') return 'rounded-[10px]';
    else if (buttonStyle === 'noCorner') return 'rounded-none';
    else return 'rounded-[38px]';
  };

  const getPlaceholder = useCallback(() => {
    const inputLabel =
      serialModule?.inputLabel ?? t('serialNumberFilledInputPlaceholder');
    if (serialNumber?.length > 0) return inputLabel;
    return `${t('enterText')} ${inputLabel?.toLowerCase()}...`;
  }, [serialNumber, serialModule, t]);

  const buttonDisabled = loading || !redirectRoute;

  return (
    <section
      className={`relative w-full flex flex-col gap-3 bg-white shadow-md p-4 ${getCornerRadius()}`}
    >
      <Input
        value={serialNumber}
        placeholderStyle='!z-20'
        placeholder={getPlaceholder()}
        onChange={(event) => {
          // Clear the cached route synchronously so the button cannot open
          // a tab for a stale serial number while new input is being
          // validated.
          setRedirectRoute(null);
          setSerialError('');
          setSerialNumber(event.target.value);
        }}
        onKeyDown={(event) => {
          // Enter inside an input is still a user gesture, so firing
          // handleRedirect synchronously from here is safe on Safari iOS.
          if (event.key === 'Enter' && redirectRoute) handleRedirect();
        }}
      />
      {serialError && (
        <span className='px-4 text-warning text-xs -mt-2.5'>{serialError}</span>
      )}
      <ProductButton
        loading={loading}
        disabled={buttonDisabled}
        buttonStyle={buttonStyle}
        mediaLink={serialModule.image || ''}
        textColor={serialModule.color || 'white'}
        onClick={handleRedirect}
        background={serialModule.backgroundColor || brandTheme}
        hideImage={serialModule.hideImage || !serialModule.image}
        title={serialModule.text || t('defaultSerialNumberButtonTitle')}
        variant={
          serialModule.hideImage || !serialModule.image ? 'compact' : 'default'
        }
      />
    </section>
  );
};

export default SerialRouter;
